import { useEffect, useRef } from 'react';
import styles from './CreateRegionModal.module.css';
import { useCreateRegionModal } from '@/features/parameters/hooks/useCreateRegionModal';
import { RegionDTO } from '@/features/parameters/types/parameters.types';
import { EditRegionLeftPanel } from '../../Panels/EditRegionLeftPanel';
import { MapRightPanel } from '../../Panels/MapRightPanel';
import { SelectBaseLeftPanel } from '../../Panels/SelectBaseLeftPanel';
import { ModalHeader } from './ModalHeader';

interface CreateRegionModalProps {
	isOpen: boolean;
	regions: RegionDTO[];
	onClose: () => void;
	onConfirm: (name: string, stateCodes: string[]) => void;
	initialRegion?: RegionDTO;
}

const CreateRegionModalContent = ({
	regions,
	onClose,
	onConfirm,
	initialRegion,
}: Omit<CreateRegionModalProps, 'isOpen'>) => {
	const overlayRef = useRef<HTMLDivElement>(null);

	const {
		step,
		isEditMode,
		selectedStateCodes,
		regionName,
		searchQuery,
		handleSelectBase,
		handleSkipBase,
		handleToggleState,
		setSearchQuery,
		setRegionName,
		handleConfirm,
	} = useCreateRegionModal(onConfirm, initialRegion);

	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKey);
		return () => document.removeEventListener('keydown', handleKey);
	}, [onClose]);

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, []);

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === overlayRef.current) onClose();
	};

	return (
		<div
			ref={overlayRef}
			className={styles.overlay}
			onClick={handleOverlayClick}
			role="dialog"
			aria-modal="true"
			aria-label={isEditMode ? 'Editar Região' : 'Criar Nova Região'}
		>
			<div className={styles.modal}>
				<ModalHeader onClose={onClose} isEditMode={isEditMode} />

				<div className={styles.body}>
					<div className={styles.leftPanel}>
						{step === 'selectBase' ? (
							<SelectBaseLeftPanel
								regions={regions}
								searchQuery={searchQuery}
								previewedRegionId={null}
								onSearchChange={setSearchQuery}
								onPreviewRegion={handleSelectBase}
							/>
						) : (
							<EditRegionLeftPanel
								selectedStateCodes={selectedStateCodes}
								searchQuery={searchQuery}
								onSearchChange={setSearchQuery}
								onToggleState={handleToggleState}
							/>
						)}
					</div>

					<div className={styles.divider} />

					<div className={styles.rightPanel}>
						<MapRightPanel
							step={step}
							regionName={regionName}
							selectedStateCodes={step === 'selectBase' ? [] : selectedStateCodes}
							isEditMode={isEditMode}
							onNameChange={setRegionName}
							onSkip={handleSkipBase}
							onConfirm={handleConfirm}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export const CreateRegionModal = ({
	isOpen,
	regions,
	onClose,
	onConfirm,
	initialRegion,
}: CreateRegionModalProps) => {
	if (!isOpen) return null;

	return (
		<CreateRegionModalContent
			regions={regions}
			onClose={onClose}
			onConfirm={onConfirm}
			initialRegion={initialRegion}
		/>
	);
};
